// Servicio de OpenAI para el asistente inteligente de la veterinaria
import { patientsService, inventoryService, prescriptionsService, historiaClinicaService } from './firebaseServices';

class OpenAIAssistantService {
  constructor() {
    // Configurar la API key desde las variables de entorno
    this.apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    this.apiUrl = 'https://api.openai.com/v1/chat/completions';

    // Caché en memoria para evitar recargar Firebase en cada mensaje
    this._cacheDatos = null;
    this._cacheDatosTs = 0;
    this._cacheTTL = 120000; // 2 minutos

    console.log('🚀 Asistente IA inicializado');
    console.log('🔑 API Key presente:', this.apiKey ? 'SÍ' : 'NO');
    
    if (!this.apiKey) {
      console.warn('⚠️ OpenAI API key no configurada. El asistente funcionará en modo básico.');
    } else {
      console.log('✅ OpenAI API key configurada correctamente');
    }
  }

  // Invalida la caché (útil tras crear/editar pacientes o productos)
  invalidarCache() {
    this._cacheDatos = null;
    this._cacheDatosTs = 0;
  }

  // Función para obtener datos de pacientes, productos e inventario
  async obtenerDatosVeterinaria(opciones = {}) {
    const { forzar = false } = opciones;

    // Servir desde caché si está vigente para no recargar Firebase en cada mensaje
    const ahora = Date.now();
    if (!forzar && this._cacheDatos && (ahora - this._cacheDatosTs) < this._cacheTTL) {
      console.log('⚡ Datos servidos desde caché (sin recargar Firebase)');
      return this._cacheDatos;
    }

    try {
      console.log('📊 Cargando datos completos de Firebase...');

      // Obtener pacientes, TODAS las recetas y TODAS las historias clínicas en paralelo
      // (una sola consulta de cada colección en lugar de una por paciente).
      const [pacientesResult, recetasResult, historiasResult] = await Promise.all([
        patientsService.getAllPatients(),
        prescriptionsService.getAllPrescriptions().catch((e) => {
          console.warn('⚠️ No se pudieron cargar las recetas:', e?.message);
          return { success: false, data: [] };
        }),
        historiaClinicaService.getAllHistoriasClinicas().catch((e) => {
          console.warn('⚠️ No se pudieron cargar las historias clínicas:', e?.message);
          return { success: false, data: [] };
        })
      ]);
      const pacientes = pacientesResult.success ? pacientesResult.data : [];

      // Agrupar las historias clínicas por pacienteId una sola vez
      const todasLasHistorias = (historiasResult && historiasResult.success && Array.isArray(historiasResult.data))
        ? historiasResult.data
        : [];
      const historiasPorPacienteId = new Map();
      for (const h of todasLasHistorias) {
        const pid = h.pacienteId || h.paciente_id;
        if (pid) {
          if (!historiasPorPacienteId.has(pid)) historiasPorPacienteId.set(pid, []);
          historiasPorPacienteId.get(pid).push(h);
        }
      }
      // Ordenar cada grupo por fecha (más reciente primero)
      for (const lista of historiasPorPacienteId.values()) {
        lista.sort((a, b) => new Date(b.fecha || b.fechaCreacion || 0) - new Date(a.fecha || a.fechaCreacion || 0));
      }

      // Agrupar las recetas por pacienteId (y por nombre como respaldo) una sola vez
      const todasLasRecetas = (recetasResult && recetasResult.success && Array.isArray(recetasResult.data))
        ? recetasResult.data
        : [];
      const recetasPorPacienteId = new Map();
      const recetasPorNombre = new Map();
      for (const r of todasLasRecetas) {
        const pid = r.pacienteId || r.paciente_id;
        if (pid) {
          if (!recetasPorPacienteId.has(pid)) recetasPorPacienteId.set(pid, []);
          recetasPorPacienteId.get(pid).push(r);
        }
        const nombreRef = (r.pacienteName || r.paciente || r.nombrePaciente || r.nombre_paciente || '').toLowerCase();
        if (nombreRef) {
          if (!recetasPorNombre.has(nombreRef)) recetasPorNombre.set(nombreRef, []);
          recetasPorNombre.get(nombreRef).push(r);
        }
      }

      console.log('🐕 Pacientes encontrados:', pacientes.length, '| 💊 Recetas totales:', todasLasRecetas.length);

      // Crear resumen completo de pacientes con todos los campos disponibles
      // Usar for...of en lugar de map para poder usar await para cargar recetas
      const resumenPacientes = [];
      for (const p of pacientes) {
        console.log('🔍 Procesando paciente:', p.nombre);
        console.log('🎂 Edad original del campo:', p.edad);
        console.log('🎈 Fecha nacimiento:', p.fechaNacimiento);
        
        // Función para convertir timestamps de Firebase a fechas legibles
        const convertirFecha = (fecha, esUltimaVisita = false) => {
          if (!fecha) return 'No registrada';
          
          if (esUltimaVisita) {
            console.log('🔍 Procesando última visita:', fecha);
          }
          
          try {
            let fechaConvertida;
            
            // Si es un timestamp de Firebase con seconds
            if (fecha && typeof fecha === 'object' && fecha.seconds) {
              fechaConvertida = new Date(fecha.seconds * 1000);
            }
            // Si es un timestamp de Firebase con _seconds
            else if (fecha && typeof fecha === 'object' && fecha._seconds) {
              fechaConvertida = new Date(fecha._seconds * 1000);
            }
            // Si es un número (timestamp en segundos)
            else if (typeof fecha === 'number') {
              fechaConvertida = new Date(fecha * 1000);
            }
            // Si es una fecha normal
            else if (typeof fecha === 'string' || fecha instanceof Date) {
              fechaConvertida = new Date(fecha);
            }
            // Si es otro tipo de objeto con timestamp
            else if (fecha && fecha.toString && fecha.toString().includes('Timestamp')) {
              // Extraer el número del timestamp
              const match = fecha.toString().match(/seconds=(\d+)/);
              if (match) {
                const seconds = parseInt(match[1]);
                fechaConvertida = new Date(seconds * 1000);
              }
            }
            else {
              return fecha.toString();
            }
            
            // Verificar si la fecha es válida
            if (!fechaConvertida || isNaN(fechaConvertida.getTime())) {
              return 'Fecha inválida';
            }
            
            // Formatear según el contexto
            if (esUltimaVisita) {
              return fechaConvertida.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            } else {
              return fechaConvertida.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            }
            
          } catch (error) {
            console.log('⚠️ Error convirtiendo fecha:', error, 'Fecha original:', fecha);
            return 'Error en fecha';
          }
        };

        // Calcular edad actual basada en fecha de nacimiento
        let edadCalculada = 'No registrada';
        const fechaNacimientoOriginal = p.fechaNacimiento || p.birthDate || p.dateOfBirth || p.nacimiento || p.birth_date || p.fecha_nacimiento || p.fechaDeNacimiento;
        
        console.log('🎂 Calculando edad para:', p.nombre, 'con fecha:', fechaNacimientoOriginal);
        
        if (fechaNacimientoOriginal && fechaNacimientoOriginal !== 'No registrada') {
          try {
            let fechaNac;
            
            // Convertir el timestamp de Firebase a Date
            if (fechaNacimientoOriginal && typeof fechaNacimientoOriginal === 'object' && fechaNacimientoOriginal.seconds) {
              console.log('🕐 Convirtiendo timestamp Firebase para edad:', fechaNacimientoOriginal.seconds);
              fechaNac = new Date(fechaNacimientoOriginal.seconds * 1000);
            }
            else if (fechaNacimientoOriginal && typeof fechaNacimientoOriginal === 'object' && fechaNacimientoOriginal._seconds) {
              console.log('🕐 Convirtiendo timestamp Firebase _seconds para edad:', fechaNacimientoOriginal._seconds);
              fechaNac = new Date(fechaNacimientoOriginal._seconds * 1000);
            }
            else if (typeof fechaNacimientoOriginal === 'number') {
              console.log('🔢 Convirtiendo timestamp numérico para edad:', fechaNacimientoOriginal);
              fechaNac = new Date(fechaNacimientoOriginal * 1000);
            }
            else if (fechaNacimientoOriginal && fechaNacimientoOriginal.toString && fechaNacimientoOriginal.toString().includes('Timestamp')) {
              console.log('🏷️ Timestamp como string para edad:', fechaNacimientoOriginal.toString());
              const match = fechaNacimientoOriginal.toString().match(/seconds=(\d+)/);
              if (match) {
                const seconds = parseInt(match[1]);
                console.log('🕐 Timestamp extraído para edad:', seconds);
                fechaNac = new Date(seconds * 1000);
              }
            }
            else {
              console.log('📅 Usando fecha directamente para edad:', fechaNacimientoOriginal);
              fechaNac = new Date(fechaNacimientoOriginal);
            }
            
            if (!fechaNac || isNaN(fechaNac.getTime())) {
              console.log('❌ Fecha de nacimiento inválida para:', p.nombre);
              edadCalculada = p.edad || 'No registrada';
            } else {
              // Verificar que la fecha esté en un rango lógico (no más de 50 años atrás, no en el futuro)
              const hoy = new Date();
              const hace50Anos = new Date(hoy.getFullYear() - 50, hoy.getMonth(), hoy.getDate());
              const manana = new Date(hoy.getTime() + 24 * 60 * 60 * 1000);
              
              if (fechaNac < hace50Anos) {
                console.log('⚠️ Fecha muy antigua (más de 50 años):', fechaNac);
                console.log('🔄 Posible error en timestamp, intentando conversión alternativa');
                // Tal vez el timestamp está en milisegundos en lugar de segundos
                if (fechaNacimientoOriginal.seconds && fechaNacimientoOriginal.seconds > 1000000000000) {
                  fechaNac = new Date(fechaNacimientoOriginal.seconds);
                  console.log('🔄 Fecha corregida (timestamp en ms):', fechaNac);
                }
              } else if (fechaNac > manana) {
                console.log('⚠️ Fecha en el futuro:', fechaNac);
                edadCalculada = p.edad || 'Fecha inválida';
                return edadCalculada;
              }
              
              // Calcular años de forma más precisa
              let años = hoy.getFullYear() - fechaNac.getFullYear();
              const mesCumple = fechaNac.getMonth();
              const diaCumple = fechaNac.getDate();
              const mesActual = hoy.getMonth();
              const diaActual = hoy.getDate();
              
              // Ajustar si no ha cumplido años este año
              const noCumplioEsteAno = mesActual < mesCumple || (mesActual === mesCumple && diaActual < diaCumple);
              if (noCumplioEsteAno) {
                años--;
              }
              
              console.log('✅ EDAD CALCULADA FINAL para', p.nombre + ':', años, 'años (nació en', fechaNac.getFullYear() + ')');
              edadCalculada = `${años} años`;
            }
          } catch (error) {
            console.log('⚠️ Error calculando edad para:', p.nombre, error);
            edadCalculada = p.edad || 'No registrada';
          }
        } else {
          edadCalculada = p.edad || 'No registrada';
        }
        
        console.log('📤 ENVIANDO AL ASISTENTE:', p.nombre, '- Edad:', edadCalculada);
        
        const pacienteProcesado = {
          nombre: p.nombre || 'Sin nombre',
          especie: p.especie || 'No especificada', 
          raza: p.raza || 'No especificada',
          edad: edadCalculada, // Usar edad calculada
          peso: p.peso || 'No registrado',
          genero: p.genero || p.sexo || 'No especificado',
          color: p.color || 'No especificado',
          propietario: p.propietario || p.owner || 'No especificado',
          propietarioApellido: p.propietarioApellido || p.ownerLastName || 'No disponible',
          propietarioCompleto: `${p.propietario || ''} ${p.propietarioApellido || ''}`.trim() || 'No especificado',
          telefono: p.propietarioTelefono || p.telefono || p.phone || p.celular || 'No disponible',
          email: p.propietarioEmail || p.email || 'No disponible',
          direccion: p.propietarioDireccion || p.direccion || p.address || 'No disponible',
          ciudad: p.propietarioCiudad || p.ciudad || p.city || 'No disponible',
          cedula: p.propietarioCedula || p.cedula || p.id || 'No disponible',
          estado: p.estado || p.status || 'activo',
          ultimaVisita: convertirFecha(p.ultimaVisita || p.lastVisit, true), // true indica que es última visita
          fechaNacimiento: convertirFecha(p.fechaNacimiento || p.birthDate, false),
          observaciones: p.observaciones || p.notes || p.comentarios || 'Sin observaciones',
          // Motivo de consulta - Campo IMPORTANTE
          motivoConsulta: p.motivoConsulta || p.razonConsulta || p.razon || p.chiefComplaint || p.chief_complaint || p.motivo || p.razon_consulta || p.motivodeConsulta || 'No registrado',
          // Anamnesis - Campo IMPORTANTE (historial clínico y antecedentes)
          anamnesis: p.anamnesis || p.anamnesico || p.antecedentes || p.background || p.historia_clinica || p.historiaMedica || p.historialClinico || 'No registrada',
          // Contacto de emergencia
          contactoEmergenciaNombre: p.contactoEmergenciaNombre || 'No disponible',
          contactoEmergenciaTelefono: p.contactoEmergenciaTelefono || 'No disponible',
          contactoEmergenciaRelacion: p.contactoEmergenciaRelacion || 'No disponible',
          // Historia clínica completa
          vacunas: Array.isArray(p.vacunas) ? p.vacunas : (p.vacunas ? [p.vacunas] : []),
          tratamientos: Array.isArray(p.tratamientos) ? p.tratamientos : (p.tratamientos ? [p.tratamientos] : []),
          historialMedico: Array.isArray(p.historialMedico) ? p.historialMedico : (p.historialMedico ? [p.historialMedico] : []),
          diagnosticos: Array.isArray(p.diagnosticos) ? p.diagnosticos : (p.diagnosticos ? [p.diagnosticos] : []),
          cirugias: Array.isArray(p.cirugias) ? p.cirugias : (p.cirugias ? [p.cirugias] : []),
          hospitalizaciones: Array.isArray(p.hospitalizaciones) ? p.hospitalizaciones : (p.hospitalizaciones ? [p.hospitalizaciones] : []),
          consultasPrevias: Array.isArray(p.consultasPrevias) ? p.consultasPrevias : (p.consultasPrevias ? [p.consultasPrevias] : []),
          medicamentosActuales: Array.isArray(p.medicamentosActuales) ? p.medicamentosActuales : (p.medicamentosActuales ? [p.medicamentosActuales] : []),
          examenesLaboratorio: Array.isArray(p.examenesLaboratorio) ? p.examenesLaboratorio : (p.examenesLaboratorio ? [p.examenesLaboratorio] : []),
          alergias: p.alergias || p.allergies || 'Ninguna conocida',
          condicionesPreexistentes: Array.isArray(p.condicionesPreexistentes) ? p.condicionesPreexistentes : (p.condicionesPreexistentes ? [p.condicionesPreexistentes] : []),
          // Información adicional
          microchip: p.microchip || p.chip || 'No',
          esterilizado: p.esterilizado || p.sterilized || p.castrado || 'No especificado'
        };

        // Asignar recetas desde los mapas precalculados (sin consultas adicionales)
        const recetasDelPaciente = recetasPorPacienteId.get(p.id)
          || (p.nombre ? recetasPorNombre.get(p.nombre.toLowerCase()) : null)
          || [];
        pacienteProcesado.recetas = recetasDelPaciente;

        // Asignar historias clínicas (expediente) desde el mapa precalculado
        pacienteProcesado.historiasClinicas = historiasPorPacienteId.get(p.id) || [];
        console.log('💊 Recetas para', p.nombre + ':', recetasDelPaciente.length,
          '| 📋 Historias:', pacienteProcesado.historiasClinicas.length);

        resumenPacientes.push(pacienteProcesado);
      }

      // Obtener datos de productos/inventario (simulado por ahora)
      const productos = await this.obtenerProductosInventario();

      const datosCompletos = {
        pacientes: resumenPacientes,
        productos: productos,
        totalPacientes: pacientes.length,
        pacientesActivos: pacientes.filter(p => p.estado === 'activo').length,
        pacientesEnTratamiento: pacientes.filter(p => p.estado === 'en_tratamiento').length,
        pacientesCriticos: pacientes.filter(p => p.estado === 'critico').length,
        pacientesInactivos: pacientes.filter(p => p.estado === 'inactivo').length,
        totalProductos: productos.length,
        productosStockBajo: productos.filter(p => p.cantidad <= p.cantidadMinima).length,
        productosDisponibles: productos.filter(p => p.cantidad > p.cantidadMinima).length,
        categoriasMasComunes: this.calcularCategoriasMasComunes(productos),
        // Estadísticas adicionales
        especiesMasComunes: this.calcularEspeciesMasComunes(pacientes),
        razasMasComunes: this.calcularRazasMasComunes(pacientes),
        edadPromedio: this.calcularEdadPromedio(pacientes)
      };

      console.log('✅ Datos completos cargados:', {
        pacientes: datosCompletos.totalPacientes,
        productos: datosCompletos.totalProductos
      });

      // Guardar en caché para los próximos mensajes
      this._cacheDatos = datosCompletos;
      this._cacheDatosTs = Date.now();

      return datosCompletos;
    } catch (error) {
      console.error('Error obteniendo datos de la veterinaria:', error);
      return {
        pacientes: [],
        productos: [],
        totalPacientes: 0,
        pacientesActivos: 0,
        pacientesEnTratamiento: 0,
        pacientesCriticos: 0,
        pacientesInactivos: 0,
        totalProductos: 0,
        especiesMasComunes: [],
        razasMasComunes: [],
        edadPromedio: 'No calculable'
      };
    }
  }

  // Funciones auxiliares para estadísticas
  calcularEspeciesMasComunes(pacientes) {
    const especies = {};
    pacientes.forEach(p => {
      if (p.especie && p.especie !== 'No especificada') {
        especies[p.especie] = (especies[p.especie] || 0) + 1;
      }
    });
    return Object.entries(especies).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }

  calcularRazasMasComunes(pacientes) {
    const razas = {};
    pacientes.forEach(p => {
      if (p.raza && p.raza !== 'No especificada') {
        razas[p.raza] = (razas[p.raza] || 0) + 1;
      }
    });
    return Object.entries(razas).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }

  calcularEdadPromedio(pacientes) {
    const pacientesConEdad = pacientes.filter(p => p.edad && !isNaN(p.edad));
    if (pacientesConEdad.length === 0) return 'No calculable';
    const sumaEdades = pacientesConEdad.reduce((suma, p) => suma + parseFloat(p.edad), 0);
    return (sumaEdades / pacientesConEdad.length).toFixed(1) + ' años';
  }

  // Función para obtener productos e inventario (placeholder para integración futura)
  async obtenerProductosInventario() {
    try {
      console.log('🛍️ Cargando inventario desde Firebase...');
      console.log('🔧 inventoryService disponible:', typeof inventoryService);
      const inventarioResult = await inventoryService.getAllInventoryItems();
      console.log('📋 Resultado completo:', inventarioResult);
      
      if (inventarioResult.success) {
        const productos = inventarioResult.data.map(item => {
          console.log('🔍 Procesando producto:', item.nombre);
          console.log('📊 Cantidad original:', item.cantidad, '(tipo:', typeof item.cantidad, ')');
          if (item.nombre && item.nombre.toLowerCase().includes('viscum')) {
            console.log('🔥 VISCUM - TODOS LOS CAMPOS EN FIREBASE:');
            console.log(JSON.stringify(item, null, 2));
            console.log('🔥 VISCUM - Campos relacionados con precios:');
            Object.keys(item).forEach(key => {
              if (key.toLowerCase().includes('precio') || key.toLowerCase().includes('cost') || 
                  key.toLowerCase().includes('price') || key.toLowerCase().includes('venta') ||
                  key.toLowerCase().includes('compra') || key.toLowerCase().includes('costo')) {
                console.log(`   ${key}: ${item[key]}`);
              }
            });
          }
          const cantidadFinal = item.cantidad !== undefined && item.cantidad !== null ? Number(item.cantidad) : 0;
          const cantidadMinimaFinal = item.cantidadMinima !== undefined && item.cantidadMinima !== null ? Number(item.cantidadMinima) : 0;
          console.log('📊 Cantidad procesada:', cantidadFinal);
          
          return {
            id: item.id,
            nombre: item.nombre || 'Sin nombre',
            descripcion: item.descripcion || 'Sin descripción',
            categoria: item.categoria || 'Sin categoría',
            subcategoria: item.subcategoria || '',
            marca: item.marca || 'Sin marca',
            presentacion: item.presentacion || '',
            cantidad: cantidadFinal,
            cantidadMinima: cantidadMinimaFinal,
            unidad: item.unidad || 'unidad',
            precio: Number(item.precioVenta || item.precio || 0),
            precioCompra: Number(item.precioCompra || item.costo || item.precio_compra || 0),
            proveedor: item.proveedor || 'Sin proveedor',
            codigoBarras: item.codigoBarras || '',
            lote: item.lote || '',
            fechaVencimiento: this.convertirFecha(item.fechaVencimiento) || 'No especificada',
            ubicacion: item.ubicacion || 'No especificada',
            observaciones: item.observaciones || '',
            estado: cantidadFinal <= cantidadMinimaFinal ? 'STOCK BAJO' : 'DISPONIBLE',
            activo: item.activo !== false
          };
        });
        
        // Log de productos con precios procesados
        productos.forEach(p => {
          if (p.nombre.toLowerCase().includes('viscum')) {
            console.log(`🔥 VISCUM PROCESADO FINAL: Venta=$${p.precio}, Compra=$${p.precioCompra}`);
          }
          if (p.precio > 0 || p.precioCompra > 0) {
            console.log(`💰 ${p.nombre}: Venta=$${p.precio}, Compra=$${p.precioCompra}`);
          }
        });
        
        console.log('✅ Productos cargados:', productos.length);
        return productos;
      } else {
        console.warn('⚠️ Error cargando inventario:', inventarioResult.error);
        console.warn('🔄 Usando productos de ejemplo como respaldo');
        return this.obtenerProductosEjemplo();
      }
    } catch (error) {
      console.error('❌ Error obteniendo productos:', error);
      console.error('🔄 Usando productos de ejemplo como respaldo');
      return this.obtenerProductosEjemplo();
    }
  }

  // Función de respaldo con productos de ejemplo
  obtenerProductosEjemplo() {
    console.log('⚠️ Usando productos de ejemplo - Firebase no disponible');
    return [
      { nombre: 'Vacuna Antirrábica', categoria: 'Vacunas', cantidad: 25, cantidadMinima: 10, precio: 35000, descripcion: 'Vacuna para prevención de rabia', marca: 'VetPharm', activo: true },
      { nombre: 'Desparasitante Oral', categoria: 'Medicamentos', cantidad: 15, cantidadMinima: 5, precio: 28000, descripcion: 'Medicamento antiparasitario oral', marca: 'PetMed', activo: true },
      { nombre: 'Alimento Premium Perro', categoria: 'Alimentos', cantidad: 8, cantidadMinima: 10, precio: 85000, descripcion: 'Alimento balanceado para perros', marca: 'DogChow', activo: true },
      { nombre: 'Collar Antipulgas', categoria: 'Accesorios', cantidad: 12, cantidadMinima: 5, precio: 45000, descripcion: 'Collar con repelente de pulgas', marca: 'FleeGuard', activo: true },
      { nombre: 'Vitaminas Caninas', categoria: 'Suplementos', cantidad: 20, cantidadMinima: 8, precio: 52000, descripcion: 'Suplemento vitamínico para perros', marca: 'VitaPet', activo: true },
      { nombre: 'Antibiótico Amoxicilina', categoria: 'Medicamentos', cantidad: 3, cantidadMinima: 5, precio: 42000, descripcion: 'Antibiótico para infecciones', marca: 'VetPharm', activo: true },
      { nombre: 'Vacuna Triple Felina', categoria: 'Vacunas', cantidad: 18, cantidadMinima: 12, precio: 38000, descripcion: 'Vacuna triple para gatos', marca: 'FelineVac', activo: true },
      { nombre: 'Shampoo Medicado', categoria: 'Higiene', cantidad: 7, cantidadMinima: 6, precio: 32000, descripcion: 'Shampoo para problemas dermatológicos', marca: 'CleanPet', activo: true },
      { nombre: 'Suero Fisiológico', categoria: 'Medicamentos', cantidad: 30, cantidadMinima: 15, precio: 8500, descripcion: 'Suero para hidratación', marca: 'MedSol', activo: true },
      { nombre: 'Jeringa Desechable 5ml', categoria: 'Material Médico', cantidad: 2, cantidadMinima: 50, precio: 1200, descripcion: 'Jeringas estériles desechables', marca: 'MedSupply', activo: true },
      { nombre: 'Alimento Gato Senior', categoria: 'Alimentos', cantidad: 14, cantidadMinima: 8, precio: 78000, descripcion: 'Alimento para gatos mayores', marca: 'CatLife', activo: true },
      { nombre: 'Antiinflamatorio Metacam', categoria: 'Medicamentos', cantidad: 6, cantidadMinima: 8, precio: 95000, descripcion: 'Antiinflamatorio para dolor articular', marca: 'PainRelief', activo: true }
    ];
  }

  // Función auxiliar para convertir fechas (reutilizada)
  convertirFecha(fecha) {
    if (!fecha) return null;
    
    try {
      let fechaObj;
      
      if (fecha && typeof fecha === 'object' && fecha.seconds) {
        fechaObj = new Date(fecha.seconds * 1000);
      } else if (fecha && typeof fecha === 'object' && fecha._seconds) {
        fechaObj = new Date(fecha._seconds * 1000);
      } else if (typeof fecha === 'string') {
        fechaObj = new Date(fecha);
      } else {
        fechaObj = new Date(fecha);
      }
      
      return isNaN(fechaObj.getTime()) ? null : fechaObj.toLocaleDateString('es-ES');
    } catch (error) {
      console.warn('Error convirtiendo fecha:', fecha, error);
      return null;
    }
  }

  // Crear prompt simple y efectivo
  crearPromptSistema(datosVeterinaria, incluirListaPacientes = true) {
    const fechaActual = new Date();
    const añoActual = fechaActual.getFullYear();

    // Calcular productos con stock bajo
    const productosStockBajo = datosVeterinaria.productos.filter(p => p.cantidad <= p.cantidadMinima).length;
    const productosDisponibles = datosVeterinaria.productos.filter(p => p.cantidad > p.cantidadMinima).length;

    // Crear lista completa de pacientes con motivo de consulta, anamnesis y recetas para el prompt base.
    // En consultas de inventario se omite para ahorrar tokens (la sección de pacientes no es necesaria).
    const listaPacientesBase = incluirListaPacientes
      ? datosVeterinaria.pacientes.map(p => {
          const motivo = p.motivoConsulta && p.motivoConsulta !== 'No registrado' ? ` - Motivo: ${p.motivoConsulta}` : '';
          const anamnesisText = p.anamnesis && p.anamnesis !== 'No registrada' ? ` - Anamnesis: ${p.anamnesis}` : '';
          const recetasText = Array.isArray(p.recetas) && p.recetas.length > 0 ? ` - Recetas: ${p.recetas.length} ${p.recetas.length === 1 ? 'receta' : 'recetas'}` : '';
          return `• ${p.nombre} (${p.especie}, ${p.raza}, ${p.edad}, ${p.propietarioCompleto})${motivo}${anamnesisText}${recetasText}`;
        }).join('\n')
      : '(Lista de pacientes omitida en consultas de inventario)';

    return `Eres un asistente IA especializado para la veterinaria "Belkysveterinaria".

📈 ESTADÍSTICAS GENERALES:
- Pacientes: ${datosVeterinaria.totalPacientes} total (${datosVeterinaria.pacientesActivos} activos, ${datosVeterinaria.pacientesEnTratamiento} en tratamiento, ${datosVeterinaria.pacientesCriticos} críticos)
- Inventario: ${datosVeterinaria.totalProductos} productos (${productosDisponibles} disponibles, ${productosStockBajo} con stock bajo)

📅 CONTEXTO TEMPORAL:
- Fecha actual: ${fechaActual.toLocaleDateString('es-ES')}
- Año actual: ${añoActual}

🐾 PACIENTES REGISTRADOS:
${listaPacientesBase || '⚠️ No hay pacientes registrados en el sistema'}

🚨 REGLAS OBLIGATORIAS - NO VIOLAR:
1. Para PACIENTES: Cuando veas "🎂 EDAD ACTUAL CALCULADA: X años" - USA ESA EDAD EXACTA
2. Para PRODUCTOS: Usar precios y cantidades exactas de la base de datos
3. PROHIBIDO calcular o modificar datos - usar exactamente como aparecen
4. Para stock bajo: alertar cuando cantidad <= cantidad mínima
5. Solo responde con datos que aparecen en la base de datos
6. Responde de forma directa, precisa y profesional en español
7. Incluye información relevante sobre disponibilidad y precios cuando consulten productos
8. Si preguntan por pacientes, consulta la lista de PACIENTES REGISTRADOS arriba`;
  }

  // Función principal optimizada para reducir tokens
  async chat(mensaje, historialConversacion = []) {
    console.log('💬 Procesando mensaje:', mensaje);
    console.log('🔑 API Key disponible:', this.apiKey ? 'SÍ' : 'NO');
    
    try {
      // Si no hay API key, usar respuesta simulada directamente
      if (!this.apiKey) {
        console.log('🔄 Sin API key, usando modo básico...');
        return await this.respuestaSimulada(mensaje);
      }
      
      // Obtener datos actuales de la veterinaria
      console.log('📊 Obteniendo datos de la veterinaria...');
      const datosVeterinaria = await this.obtenerDatosVeterinaria();
      console.log('📋 Datos cargados - Pacientes:', datosVeterinaria.totalPacientes);
      console.log('👥 Nombres de pacientes:', datosVeterinaria.pacientes.map(p => p.nombre).join(', ') || 'NINGUNO');
      
      // Analizar el mensaje para determinar qué información incluir
      // (se pasan los datos para reconocer nombres reales de productos y pacientes)
      const tipoConsulta = this.analizarTipoConsulta(mensaje, datosVeterinaria);
      console.log('🎯 Tipo de consulta detectado:', tipoConsulta);
      
      const promptOptimizado = this.crearPromptOptimizado(datosVeterinaria, tipoConsulta, mensaje);
      
      // Preparar mensajes para la API
      const mensajes = [
        {
          role: 'system',
          content: promptOptimizado
        },
        ...historialConversacion,
        {
          role: 'user',
          content: mensaje
        }
      ];

      console.log('🚀 Enviando solicitud optimizada a OpenAI...');
      console.log('📋 PROMPT COMPLETO ENVIADO A OPENAI:');
      console.log(promptOptimizado);
      console.log('═══════════════════════════════════════');
      
      // VALIDACIÓN: Si no hay pacientes y es una búsqueda de paciente, devolver error claro
      if (tipoConsulta === 'busqueda_paciente' && datosVeterinaria.totalPacientes === 0) {
        console.log('⚠️ ADVERTENCIA: Se detectó búsqueda de pacientes pero no hay pacientes cargados');
        return {
          success: false,
          respuesta: '❌ No hay pacientes registrados en el sistema en este momento. Por favor, agregue pacientes primero antes de consultarlos.',
          tokensUsados: 0,
          debug: 'SIN_PACIENTES'
        };
      }

      // Las respuestas informativas y de recomendación pueden ser más largas
      const esInformativa = tipoConsulta === 'conocimiento_general' || tipoConsulta === 'recomendacion';
      const maxTokens = esInformativa ? 700 : 400;

      // Llamar a la API de OpenAI con timeout (más holgado para respuestas informativas)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), esInformativa ? 20000 : 10000);

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: mensajes,
          max_tokens: maxTokens,
          temperature: esInformativa ? 0.3 : 0.1
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Error de OpenAI API:', response.status, errorData);
        
        // Si hay problemas con la API (cuota excedida, etc.), usar modo básico
        console.log('🔄 Problema con OpenAI, usando modo básico...');
        const fallbackResponse = await this.respuestaSimulada(mensaje);
        return {
          ...fallbackResponse,
          respuesta: `ℹ️ Modo básico activado (problema con OpenAI API)\n\n${fallbackResponse.respuesta}`
        };
      }

      const data = await response.json();
      console.log('✅ Respuesta exitosa de OpenAI - Tokens:', data.usage?.total_tokens || 0);
      
      return {
        success: true,
        respuesta: data.choices[0].message.content.trim(),
        tokensUsados: data.usage?.total_tokens || 0
      };

    } catch (error) {
      console.error('❌ Error en chat con OpenAI:', error);
      
      // Si hay error con la API, usar respuesta simulada como fallback
      console.log('🔄 Usando modo simulado como fallback...');
      const fallbackResponse = await this.respuestaSimulada(mensaje);
      
      return {
        ...fallbackResponse,
        respuesta: fallbackResponse.respuesta
      };
    }
  }

  // Analizar tipo de consulta para optimizar tokens
  analizarTipoConsulta(mensaje, datosVeterinaria = null) {
    const mensajeLower = mensaje.toLowerCase();

    // Detectar si el mensaje nombra un paciente o producto real (cuando hay datos)
    let nombraPaciente = false;
    let nombraProducto = false;
    if (datosVeterinaria) {
      const incluyeNombre = (lista, campo) => (lista || []).some((item) => {
        const nombre = (item[campo] || '').toLowerCase().trim();
        if (nombre.length < 3) return false;
        if (mensajeLower.includes(nombre)) return true;
        // Coincidencia por palabra significativa del nombre (p. ej. "amoxicilina"
        // dentro de "Antibiótico Amoxicilina 500mg")
        return nombre.split(/\s+/).some((palabra) => palabra.length >= 4 && mensajeLower.includes(palabra));
      });
      nombraPaciente = incluyeNombre(datosVeterinaria.pacientes, 'nombre');
      nombraProducto = incluyeNombre(datosVeterinaria.productos, 'nombre');
    }

    // 0a) Intención de RECOMENDACIÓN clínica ("¿qué le doy/uso para...?", "¿qué tratamiento
    //     recomiendas para...?", "¿cómo trato...?"). Combina conocimiento veterinario con el
    //     expediente del paciente (si se nombra) y el inventario disponible.
    const marcadoresRecomendacion = [
      'qué le doy', 'que le doy', 'qué le puedo dar', 'que le puedo dar', 'qué puedo darle',
      'que puedo darle', 'qué darle', 'que darle', 'qué le aplico', 'que le aplico',
      'qué le receto', 'que le receto', 'qué le recomiendas', 'que le recomiendas',
      'qué tratamiento', 'que tratamiento', 'tratamiento para', 'tratamiento de',
      'recomiéndame', 'recomiendame', 'recomiendas', 'recomienda', 'recomendación',
      'recomendacion', 'recomendaciones', 'qué uso para', 'que uso para', 'qué usar para',
      'que usar para', 'qué usar', 'que usar', 'qué medicamento para', 'que medicamento para',
      'qué medicamento le', 'que medicamento le', 'cómo trato', 'como trato', 'cómo tratar',
      'como tratar', 'qué hago si', 'que hago si', 'qué opciones', 'que opciones',
      'sugerencias para', 'sugiéreme', 'sugiereme', 'qué le sirve para', 'manejo de',
      'manejo para', 'cómo manejo', 'como manejo', 'qué hacer con', 'que hacer con'
    ];
    const esRecomendacion = marcadoresRecomendacion.some((s) => mensajeLower.includes(s));
    if (esRecomendacion) {
      console.log('🩺 DETECTADO: Consulta de recomendación clínica:', mensajeLower);
      return 'recomendacion';
    }

    // 0b) Intención INFORMATIVA / conocimiento general ("¿para qué sirve...?", "¿qué es...?",
    //    efectos, contraindicaciones, dosis recomendada, etc.). Tiene prioridad sobre la
    //    búsqueda en inventario/pacientes, salvo que se refiera a un paciente concreto.
    const marcadoresInformativos = [
      'para qué sirve', 'para que sirve', 'para qué se usa', 'para que se usa',
      'para qué se utiliza', 'para que se utiliza', 'sirve para', 'se usa para',
      'qué es', 'que es', 'qué son', 'que son', 'qué significa', 'que significa',
      'cómo funciona', 'como funciona', 'cómo se usa', 'como se usa', 'cómo se aplica',
      'como se aplica', 'cómo actúa', 'como actua', 'efectos secundarios', 'efectos adversos',
      'reacciones adversas', 'contraindicacion', 'contraindicaciones', 'dosis recomendada',
      'dosificación', 'dosificacion', 'beneficios de', 'riesgos de', 'es seguro',
      'puede tomar', 'puede usar', 'información sobre',
      'informacion sobre', 'explícame', 'explicame', 'qué hace', 'que hace',
      'para qué se receta', 'mecanismo', 'indicaciones'
    ];
    const esInformativa = marcadoresInformativos.some((s) => mensajeLower.includes(s));
    if (esInformativa && !nombraPaciente) {
      console.log('🌐 DETECTADO: Consulta de conocimiento general/informativa:', mensajeLower);
      return 'conocimiento_general';
    }

    // 1) Coincidencia con nombres reales: si nombra un paciente concreto, priorizar paciente
    //    (puede preguntar por sus recetas/expediente); si nombra un producto, inventario.
    if (nombraPaciente) return 'busqueda_paciente';
    if (nombraProducto) return 'busqueda_productos';

    // 2) Señales fuertes de inventario/producto: tienen prioridad sobre la rama
    //    de paciente para que preguntas como "¿tienes amoxicilina?" o
    //    "precio del medicamento X" se respondan con el inventario.
    const señalesProducto = [
      'inventario', 'stock', 'existencia', 'existencias', 'precio', 'precios',
      'cuesta', 'vale', 'cuánto vale', 'cuanto vale', 'disponible', 'disponibles',
      'agotado', 'agotada', 'sin stock', 'queda', 'quedan', 'cuánto hay', 'cuanto hay',
      'cuántas hay', 'cuantas hay', 'proveedor', 'vencimiento', 'vence', 'caduca',
      'lote', 'comprar', 'vender', 'presentación', 'presentacion', 'costo', 'margen',
      'ganancia', 'producto', 'productos', 'medicamento', 'medicamentos', 'medicina',
      'medicinas', 'fármaco', 'farmaco', 'antibiótico', 'antibiotico', 'amoxicilina',
      'ankofen', 'desparasitante'
    ];
    if (señalesProducto.some((s) => mensajeLower.includes(s))) {
      console.log('🎯 DETECTADO: Consulta de productos/medicamentos para:', mensajeLower);
      return 'busqueda_productos';
    }

    // Si menciona cualquier cosa relacionada con buscar info de paciente, edad o historia clínica
    if (mensajeLower.includes('edad') || mensajeLower.includes('años') ||
        mensajeLower.includes('nacho') || mensajeLower.includes('tito') ||
        mensajeLower.includes('rocky') || mensajeLower.includes('perla') ||
        mensajeLower.includes('teffy') || mensajeLower.includes('luna') ||
        mensajeLower.includes('como está') || mensajeLower.includes('información') || 
        mensajeLower.includes('peso') || mensajeLower.includes('cuanto pesa') ||
        mensajeLower.includes('cuanto peso') || mensajeLower.includes('cual es el peso') ||
        mensajeLower.includes('color') || mensajeLower.includes('cual es el color') ||
        mensajeLower.includes('de que color') || 
        mensajeLower.includes('teléfono') || mensajeLower.includes('telefono') ||
        mensajeLower.includes('número') || mensajeLower.includes('numero') ||
        mensajeLower.includes('contacto') || mensajeLower.includes('llamar') ||
        mensajeLower.includes('celular') || mensajeLower.includes('whatsapp') ||
        mensajeLower.includes('propietario') || 
        mensajeLower.includes('teléfono') || mensajeLower.includes('datos') || 
        mensajeLower.includes('buscar') || mensajeLower.includes('paciente') ||
        mensajeLower.includes('mascotas') || mensajeLower.includes('mascota') ||
        mensajeLower.includes('animales') || mensajeLower.includes('animal') ||
        mensajeLower.includes('perros') || mensajeLower.includes('perro') ||
        mensajeLower.includes('gatos') || mensajeLower.includes('gato') ||
        mensajeLower.includes('quien es') || mensajeLower.includes('quién es') ||
        mensajeLower.includes('dime de') || mensajeLower.includes('cuéntame de') ||
        mensajeLower.includes('tenemos') || mensajeLower.includes('hay ') ||
        // Términos de historia clínica
        mensajeLower.includes('historia') || mensajeLower.includes('clínica') ||
        mensajeLower.includes('historial') || mensajeLower.includes('médico') ||
        mensajeLower.includes('diagnóstico') || mensajeLower.includes('cirugía') ||
        mensajeLower.includes('tratamiento') || mensajeLower.includes('medicamento') ||
        mensajeLower.includes('vacuna') || mensajeLower.includes('alergia') ||
        mensajeLower.includes('consulta') || mensajeLower.includes('examen') ||
        mensajeLower.includes('hospitalización') || mensajeLower.includes('condición') ||
        // Términos sobre motivo de consulta - NUEVO
        mensajeLower.includes('motivo') || mensajeLower.includes('razon') || 
        mensajeLower.includes('razón') || mensajeLower.includes('por qué') ||
        mensajeLower.includes('porqué') || mensajeLower.includes('fue') ||
        mensajeLower.includes('vino') || mensajeLower.includes('trajo') ||
        mensajeLower.includes('chief complaint') || mensajeLower.includes('queja') ||
        mensajeLower.includes('problema') || mensajeLower.includes('síntoma') ||
        mensajeLower.includes('sintoma') || mensajeLower.includes('presentó') ||
        mensajeLower.includes('presento') || mensajeLower.includes('trae') ||
        // Términos sobre Anamnesis - NUEVO
        mensajeLower.includes('anamnesis') || mensajeLower.includes('anamnesico') ||
        mensajeLower.includes('antecedentes') || mensajeLower.includes('background') ||
        mensajeLower.includes('antecedente') || mensajeLower.includes('previo') ||
        mensajeLower.includes('anterior') || mensajeLower.includes('antes de') ||
        // Términos sobre Recetas/Prescripciones - NUEVO
        mensajeLower.includes('receta') || mensajeLower.includes('recetario') ||
        mensajeLower.includes('recetó') || mensajeLower.includes('receto') ||
        mensajeLower.includes('medicinas') || mensajeLower.includes('prescripción') ||
        mensajeLower.includes('prescrito') || mensajeLower.includes('prescrita') ||
        mensajeLower.includes('qué se le recetó') || mensajeLower.includes('que se le receto') ||
        mensajeLower.includes('le dieron') || mensajeLower.includes('le dió') ||
        // Términos generales sobre pacientes
        mensajeLower.includes('pacientes') || mensajeLower.includes('cliente') ||
        mensajeLower.includes('clientes') || mensajeLower.includes('propietarios') ||
        mensajeLower.includes('existe') || mensajeLower.includes('existe algún')) {
      return 'busqueda_paciente';
    }
    
    // Si menciona productos, inventario o medicamentos
    if (mensajeLower.includes('producto') || mensajeLower.includes('inventario') ||
        mensajeLower.includes('stock') || mensajeLower.includes('disponible') ||
        mensajeLower.includes('precio') || mensajeLower.includes('cuesta') ||
        mensajeLower.includes('medicamento') || mensajeLower.includes('medicina') ||
        mensajeLower.includes('vacuna') || mensajeLower.includes('alimento') ||
        mensajeLower.includes('collar') || mensajeLower.includes('accesorio') ||
        mensajeLower.includes('suplemento') || mensajeLower.includes('vitamina') ||
        mensajeLower.includes('desparasitante') || mensajeLower.includes('antibiótico') ||
        mensajeLower.includes('vencimiento') || mensajeLower.includes('lote') ||
        mensajeLower.includes('proveedor') || mensajeLower.includes('marca') ||
        mensajeLower.includes('categoria') || mensajeLower.includes('ubicación') ||
        mensajeLower.includes('comprar') || mensajeLower.includes('vender') ||
        mensajeLower.includes('cuánto hay') || mensajeLower.includes('tenemos') ||
        mensajeLower.includes('ankofen') || mensajeLower.includes('amoxicilina') ||
        // Nuevas consultas específicas de características
        mensajeLower.includes('vencimiento') || mensajeLower.includes('expira') ||
        mensajeLower.includes('caduca') || mensajeLower.includes('cuando vence') ||
        mensajeLower.includes('presentacion') || mensajeLower.includes('presentación') ||
        mensajeLower.includes('quien provee') || mensajeLower.includes('proveedor') ||
        mensajeLower.includes('categoria') || mensajeLower.includes('categoría') ||
        mensajeLower.includes('precio de compra') || mensajeLower.includes('costo') ||
        mensajeLower.includes('margen') || mensajeLower.includes('ganancia')) {
      console.log('🎯 DETECTADO: Consulta de productos para:', mensajeLower);
      return 'busqueda_productos';
    }
    
    return 'general';
  }

  // Resume las historias clínicas (expediente) de un paciente para incluirlas en el prompt.
  // Incluye solo los campos con contenido para no desperdiciar tokens.
  formatearHistoriasClinicas(historias = []) {
    if (!Array.isArray(historias) || historias.length === 0) {
      return 'Sin historias clínicas registradas';
    }

    // Etiquetas legibles para los campos clínicos principales
    const camposTexto = [
      ['motivoConsulta', 'Motivo'],
      ['anamnesis', 'Anamnesis'],
      ['diagnostico', 'Diagnóstico'],
      ['pronostico', 'Pronóstico'],
      ['tratamientoEnCasa', 'Tratamiento en casa'],
      ['formulaMedica', 'Fórmula médica'],
      ['ayudasDiagnosticas', 'Ayudas diagnósticas'],
      ['enfermedadesPrevias', 'Enfermedades previas'],
      ['cirugiasPrevias', 'Cirugías previas'],
      ['tratamientosRecientes', 'Tratamientos recientes'],
      ['observaciones', 'Observaciones']
    ];
    const signosVitales = [
      ['peso', 'Peso'],
      ['temperatura', 'Temp'],
      ['frecuenciaCardiaca', 'FC'],
      ['frecuenciaRespiratoria', 'FR'],
      ['mucosas', 'Mucosas'],
      ['deshidratacion', 'Deshidratación'],
      ['estadoCorporal', 'Cond. corporal'],
      ['estadoConciencia', 'Conciencia']
    ];
    // Sistemas del examen físico (se reporta solo lo marcado como anormal)
    const sistemas = [
      'tegumentario', 'digestivo', 'respiratorio', 'musculoesqueletico', 'nervioso',
      'urinario', 'reproductivo', 'ganglios', 'palpacion', 'cavidadOral', 'nariz', 'ojos', 'oidos'
    ];

    const tieneValor = (v) => v !== undefined && v !== null && String(v).trim() !== '' && String(v).trim() !== 'No registrado';

    return historias.map((h, idx) => {
      const partes = [];
      const fecha = h.fecha || h.fechaCreacion || 'fecha no registrada';
      partes.push(`Consulta #${idx + 1} (${fecha})${tieneValor(h.veterinario) ? ` - Vet: ${h.veterinario}` : ''}`);

      camposTexto.forEach(([campo, etiqueta]) => {
        if (tieneValor(h[campo])) partes.push(`  ${etiqueta}: ${String(h[campo]).trim()}`);
      });

      const vitales = signosVitales
        .filter(([campo]) => tieneValor(h[campo]))
        .map(([campo, etiqueta]) => `${etiqueta}: ${String(h[campo]).trim()}`);
      if (vitales.length > 0) partes.push(`  Signos vitales: ${vitales.join(', ')}`);

      const anormales = sistemas
        .filter((s) => h[`${s}Anormal`] === true)
        .map((s) => {
          const eval_ = h[`${s}Evaluacion`];
          return `${s}${tieneValor(eval_) ? ` (${String(eval_).trim()})` : ''}`;
        });
      if (anormales.length > 0) partes.push(`  Hallazgos anormales: ${anormales.join(', ')}`);

      return partes.join('\n');
    }).join('\n\n');
  }

  // Devuelve los pacientes cuyo nombre se menciona en el mensaje
  encontrarPacientesEnMensaje(mensajeLower, pacientes = []) {
    return (pacientes || []).filter((p) => {
      const nombre = (p.nombre || '').toLowerCase().trim();
      return nombre.length >= 3 && mensajeLower.includes(nombre);
    });
  }

  // Bloque clínico compacto de un paciente para recomendaciones (datos relevantes + expediente)
  resumenClinicoPaciente(p) {
    const cond = Array.isArray(p.condicionesPreexistentes) ? p.condicionesPreexistentes.join(', ') : p.condicionesPreexistentes;
    const medsActuales = Array.isArray(p.medicamentosActuales) ? p.medicamentosActuales.join(', ') : p.medicamentosActuales;
    return [
      `PACIENTE: ${p.nombre}`,
      `ESPECIE: ${p.especie} | RAZA: ${p.raza} | EDAD: ${p.edad} | PESO: ${p.peso} | GÉNERO: ${p.genero}`,
      `ALERGIAS: ${p.alergias}`,
      `CONDICIONES_PREEXISTENTES: ${cond || 'Ninguna registrada'}`,
      `MEDICAMENTOS_ACTUALES: ${medsActuales || 'Ninguno registrado'}`,
      `MOTIVO_CONSULTA: ${p.motivoConsulta}`,
      `ANAMNESIS: ${p.anamnesis}`,
      `EXPEDIENTE:\n${this.formatearHistoriasClinicas(p.historiasClinicas)}`
    ].join('\n');
  }

  // Lista compacta del inventario (nombre, categoría, presentación, stock) para recomendaciones
  listaInventarioCompacta(productos = []) {
    if (!Array.isArray(productos) || productos.length === 0) return 'Inventario no disponible';
    return productos.map((p) => {
      const stock = (Number(p.cantidad) || 0) > 0 ? `${p.cantidad} ${p.unidad || 'und'} (${p.estado})` : 'AGOTADO';
      const presentacion = p.presentacion ? `, ${p.presentacion}` : '';
      return `• ${p.nombre} [${p.categoria}${presentacion}] - ${stock}`;
    }).join('\n');
  }

  // Crear prompt optimizado según el tipo de consulta
  crearPromptOptimizado(datosVeterinaria, tipoConsulta, mensaje) {
    // En consultas de inventario omitimos la lista de pacientes para ahorrar tokens
    let promptBase = this.crearPromptSistema(datosVeterinaria, tipoConsulta !== 'busqueda_productos');
    
    if (tipoConsulta === 'busqueda_productos') {
      // Mostrar información completa del inventario
      console.log('🔍 Construyendo prompt para productos. Total productos:', datosVeterinaria.productos.length);
      const listaProductosCompleta = datosVeterinaria.productos.map(p => {
        console.log(`📦 Procesando ${p.nombre}: cantidad=${p.cantidad}, estado=${p.estado}`);
        console.log(`💰 Precios - Venta: $${p.precio || 0}, Compra: $${p.precioCompra || 0}`);
        if (p.nombre.toLowerCase().includes('ankofen') || p.nombre.toLowerCase().includes('viscum')) {
          console.log('🔥 PRODUCTO ESPECÍFICO ENCONTRADO:', {
            nombre: p.nombre,
            cantidad: p.cantidad,
            cantidadMinima: p.cantidadMinima,
            estado: p.estado,
            precioVenta: p.precio,
            precioCompra: p.precioCompra
          });
        }
        const campos = [
          `PRODUCTO: ${p.nombre}`,
          `DESCRIPCIÓN: ${p.descripcion}`,
          `CATEGORÍA: ${p.categoria}`,
          `SUBCATEGORÍA: ${p.subcategoria}`,
          `MARCA: ${p.marca}`,
          `PRESENTACIÓN: ${p.presentacion}`,
          `CANTIDAD_DISPONIBLE: ${p.cantidad} ${p.unidad}`,
          `STOCK_MÍNIMO: ${p.cantidadMinima}`,
          `ESTADO_STOCK: ${p.estado}`,
          `PRECIO_VENTA_AL_CLIENTE: $${(p.precio || 0).toLocaleString('es-CO')}`,
          `PRECIO_COSTO_COMPRA: $${(p.precioCompra || 0).toLocaleString('es-CO')}`,
          `MARGEN_GANANCIA: ${p.precio && p.precioCompra ? ((p.precio - p.precioCompra) / p.precioCompra * 100).toFixed(1) : '0'}%`,
          `PROVEEDOR: ${p.proveedor}`,
          `CÓDIGO_BARRAS: ${p.codigoBarras}`,
          `LOTE: ${p.lote}`,
          `VENCIMIENTO: ${p.fechaVencimiento}`,
          `UBICACIÓN: ${p.ubicacion}`,
          `OBSERVACIONES: ${p.observaciones}`
        ].filter(campo => {
          const valor = campo.split(': ')[1]?.trim();
          // Campos críticos que siempre se muestran
          if (campo.includes('PRODUCTO:') || campo.includes('CANTIDAD_DISPONIBLE:') || 
              campo.includes('STOCK_MÍNIMO:') || campo.includes('PRECIO_VENTA_AL_CLIENTE:') || 
              campo.includes('PRECIO_COSTO_COMPRA:') || campo.includes('MARGEN_GANANCIA:') ||
              campo.includes('CATEGORÍA:') || campo.includes('PRESENTACIÓN:') || 
              campo.includes('PROVEEDOR:') || campo.includes('VENCIMIENTO:') || 
              campo.includes('ESTADO_STOCK:')) {
            return true;
          }
          // Para otros campos, filtrar solo si están completamente vacíos
          return valor && valor !== 'No disponible' && valor !== 'No especificada' && 
                 !valor.startsWith('Sin ') && valor !== '$0' && valor !== '0';
        }); // Mostrar información completa de productos
        return campos.join('\n');
      }).join('\n\n');
      
      return `${promptBase}

🛍️ BASE DE DATOS COMPLETA DE INVENTARIO:
${listaProductosCompleta}

📅 FECHA ACTUAL: ${new Date().toLocaleDateString('es-ES')} (noviembre 2025)

INSTRUCCIONES CRÍTICAS PARA PRODUCTOS:
1. Buscar el producto EXACTAMENTE en los datos anteriores
2. Usar ÚNICAMENTE los datos mostrados - NO INVENTAR información
3. PRECIOS - DISTINGUIR CLARAMENTE:
   • PRECIO_VENTA_AL_CLIENTE = Lo que cobras al cliente
   • PRECIO_COSTO_COMPRA = Lo que pagas al proveedor
   • MARGEN_GANANCIA = Porcentaje de utilidad
4. CUANDO PREGUNTEN POR "PRECIO":
   - Si dicen "precio" solo: dar el precio de venta
   - Si dicen "precio de compra" o "costo": dar precio de compra
   - Si dicen "margen" o "ganancia": dar el porcentaje
5. INFORMACIÓN DISPONIBLE:
   - Stock actual vs mínimo
   - Precios diferenciados (venta/compra/margen)
   - Proveedor y ubicación
   - Vencimiento y lote
   - Categoría y presentación
6. Para CANTIDAD: Si es 0 decir "agotado" o "sin stock"
7. Para VENCIMIENTO: Alertar si está próximo a vencer
8. Responder en español de forma clara y organizada`;
    }
    
    if (tipoConsulta === 'busqueda_paciente') {
      // Validar si hay pacientes
      if (!datosVeterinaria.pacientes || datosVeterinaria.pacientes.length === 0) {
        return `${promptBase}

⚠️ ADVERTENCIA: No hay pacientes registrados en el sistema actualmente.

RESPONDE AL USUARIO:
"No hay pacientes registrados en la base de datos en este momento. Por favor, agregue pacientes primero en el sistema antes de consultarlos."`;
      }

      // Mostrar información completa de todos los pacientes
      const listaPacientesCompleta = datosVeterinaria.pacientes.map((p, idx) => {
        const campos = [
          `\n═══ PACIENTE #${idx + 1} ═══`,
          `PACIENTE: ${p.nombre}`,
          `ESPECIE: ${p.especie}`,
          `RAZA: ${p.raza}`, 
          `EDAD_EXACTA: ${p.edad}`,
          `PESO: ${p.peso}`,
          `GENERO: ${p.genero}`,
          `COLOR: ${p.color}`,
          `PROPIETARIO_NOMBRE: ${p.propietario}`,
          `PROPIETARIO_APELLIDO: ${p.propietarioApellido}`,
          `PROPIETARIO_COMPLETO: ${p.propietarioCompleto}`,
          `TELEFONO: ${p.telefono}`,
          `EMAIL: ${p.email}`,
          `DIRECCION: ${p.direccion}`,
          `CIUDAD: ${p.ciudad}`,
          `CEDULA: ${p.cedula}`,
          `CONTACTO_EMERGENCIA_NOMBRE: ${p.contactoEmergenciaNombre}`,
          `CONTACTO_EMERGENCIA_TELEFONO: ${p.contactoEmergenciaTelefono}`,
          `ESTADO: ${p.estado}`,
          `MICROCHIP: ${p.microchip}`,
          `ESTERILIZADO: ${p.esterilizado}`,
          `ALERGIAS: ${p.alergias}`,
          `CONDICIONES_PREEXISTENTES: ${Array.isArray(p.condicionesPreexistentes) ? p.condicionesPreexistentes.join(', ') : p.condicionesPreexistentes}`,
          `MOTIVO_CONSULTA: ${p.motivoConsulta}`,
          `ANAMNESIS: ${p.anamnesis}`,
          `RECETAS: ${Array.isArray(p.recetas) && p.recetas.length > 0 ? p.recetas.map((r, idx) => {
            // Función auxiliar para convertir valores a strings útiles
            const formatearValor = (valor) => {
              if (!valor) return null;
              if (typeof valor === 'string') return valor.trim() || null;
              if (typeof valor === 'number') return String(valor);
              if (Array.isArray(valor)) {
                // Si es un array, intentar extraer strings útiles
                const items = valor
                  .map(item => {
                    if (typeof item === 'string') return item.trim();
                    if (typeof item === 'object' && item !== null) {
                      // Si es un objeto, extraer sus propiedades no vacías
                      const props = Object.entries(item)
                        .filter(([k, v]) => v && typeof v === 'string')
                        .map(([k, v]) => v.trim())
                        .filter(v => v && v !== '[object Object]');
                      return props.length > 0 ? props.join(', ') : null;
                    }
                    return null;
                  })
                  .filter(item => item !== null && item !== '[object Object]');
                return items.length > 0 ? items.join('; ') : null;
              }
              if (typeof valor === 'object' && valor !== null) {
                // Si es un objeto, extraer sus propiedades
                const props = Object.entries(valor)
                  .filter(([k, v]) => v && typeof v === 'string')
                  .map(([k, v]) => v.trim())
                  .filter(v => v && v !== '[object Object]');
                return props.length > 0 ? props.join(', ') : null;
              }
              return null;
            };
            
            // Buscar medicamento en múltiples campos posibles
            const medicamento = formatearValor(r.medicamento) || 
                                formatearValor(r.medicina) || 
                                formatearValor(r.nombre) || 
                                formatearValor(r.nombreMedicamento) || 
                                formatearValor(r.nombre_medicamento) || 
                                formatearValor(r.droga) || 
                                formatearValor(r.medicamentos) || 
                                formatearValor(r.medicaments) || 
                                'Medicamento';
            
            // Buscar dosis en múltiples campos posibles
            const dosis = formatearValor(r.dosis) || 
                         formatearValor(r.dose) || 
                         formatearValor(r.dosis_cantidad) || 
                         formatearValor(r.cantidad) || 
                         'No especificada';
            
            // Buscar frecuencia en múltiples campos posibles
            const frecuencia = formatearValor(r.frecuencia) || 
                              formatearValor(r.frequency) || 
                              formatearValor(r.cada) || 
                              'No especificada';
            
            // Buscar duración en múltiples campos posibles
            const duracion = formatearValor(r.duracion) || 
                            formatearValor(r.duration) || 
                            formatearValor(r.dias) || 
                            'No especificada';
            
            // Buscar observaciones
            const observaciones = formatearValor(r.observaciones) || 
                                 formatearValor(r.observations) || 
                                 formatearValor(r.notas) || 
                                 formatearValor(r.notes) || 
                                 '';
            
            // Construir receta con información útil
            let recetaStr = medicamento + ' (Dosis: ' + dosis + ', Cada: ' + frecuencia + ', Duración: ' + duracion + ')';
            
            if (observaciones) {
              recetaStr += ' - Observaciones: ' + observaciones;
            }
            
            // Si todo está vacío, intenta extraer datos de otros campos
            if ((dosis === 'No especificada' && frecuencia === 'No especificada' && duracion === 'No especificada') || medicamento === 'Medicamento') {
              // Construir lista de todos los campos no vacíos y útiles de la receta
              const camposDisponibles = Object.entries(r)
                .filter(([key, value]) => {
                  // Filtrar campos a ignorar
                  if (['id', 'pacienteId', 'paciente_id', 'fechaCreacion', 'fechaActualizacion', 'estado', 'paciente', 'pacienteName'].includes(key)) {
                    return false;
                  }
                  // Filtrar valores vacíos u objetos vacíos
                  if (!value || value === '[object Object]') {
                    return false;
                  }
                  // Filtrar si es un string vacío
                  if (typeof value === 'string' && !value.trim()) {
                    return false;
                  }
                  return true;
                })
                .map(([key, value]) => key + ': ' + formatearValor(value))
                .filter(item => item && !item.includes('null'))
                .slice(0, 4); // Mostrar máximo 4 campos
              
              if (camposDisponibles.length > 0) {
                recetaStr = 'Receta #' + (idx + 1) + ': ' + camposDisponibles.join(', ');
              } else {
                recetaStr = 'Receta #' + (idx + 1) + ': (Sin detalles especificados en la base de datos)';
              }
            }
            
            return recetaStr;
          }).join('; ') : 'Sin recetas registradas'}`,
          `VACUNAS: ${Array.isArray(p.vacunas) ? p.vacunas.join(', ') : p.vacunas}`,
          `TRATAMIENTOS_ACTUALES: ${Array.isArray(p.tratamientos) ? p.tratamientos.join(', ') : p.tratamientos}`,
          `MEDICAMENTOS_ACTUALES: ${Array.isArray(p.medicamentosActuales) ? p.medicamentosActuales.join(', ') : p.medicamentosActuales}`,
          `DIAGNOSTICOS_PREVIOS: ${Array.isArray(p.diagnosticos) ? p.diagnosticos.join(', ') : p.diagnosticos}`,
          `CIRUGIAS: ${Array.isArray(p.cirugias) ? p.cirugias.join(', ') : p.cirugias}`,
          `HOSPITALIZACIONES: ${Array.isArray(p.hospitalizaciones) ? p.hospitalizaciones.join(', ') : p.hospitalizaciones}`,
          `CONSULTAS_PREVIAS: ${Array.isArray(p.consultasPrevias) ? p.consultasPrevias.join(', ') : p.consultasPrevias}`,
          `EXAMENES_LABORATORIO: ${Array.isArray(p.examenesLaboratorio) ? p.examenesLaboratorio.join(', ') : p.examenesLaboratorio}`,
          `HISTORIAL_MEDICO: ${Array.isArray(p.historialMedico) ? p.historialMedico.join(', ') : p.historialMedico}`,
          `OBSERVACIONES: ${p.observaciones}`,
          `ULTIMA_VISITA: ${p.ultimaVisita}`,
          `HISTORIA_CLINICA (EXPEDIENTE - ${Array.isArray(p.historiasClinicas) ? p.historiasClinicas.length : 0} consulta(s)):\n${this.formatearHistoriasClinicas(p.historiasClinicas)}`
        ]; // Mostrar TODOS los campos - no filtrar nada
        return campos.join('\n');
      }).join('\n');
      
      return `${promptBase}

🐾 BASE DE DATOS COMPLETA DE PACIENTES (${datosVeterinaria.totalPacientes} PACIENTES REGISTRADOS):
${listaPacientesCompleta}

📅 FECHA ACTUAL: ${new Date().toLocaleDateString('es-ES')}

INSTRUCCIONES CRÍTICAS PARA CONSULTAS DE PACIENTES:
1. ✅ PACIENTES EXISTEN: La base de datos contiene ${datosVeterinaria.totalPacientes} paciente(s) registrado(s)
2. Buscar el paciente EXACTAMENTE en los datos anteriores
3. Para preguntas de edad: usar ÚNICAMENTE el valor que aparece en "EDAD_EXACTA"
4. Para PESO: buscar en el campo "PESO" - SIEMPRE está disponible
5. Para COLOR: buscar en el campo "COLOR" - SIEMPRE está disponible
6. Para TELEFONO / CONTACTO: buscar en el campo "TELEFONO" - INFORMACIÓN CRÍTICA, SIEMPRE DISPONIBLE
7. Para MOTIVO DE CONSULTA: buscar en el campo "MOTIVO_CONSULTA" - ESTE DATO SIEMPRE ESTÁ DISPONIBLE
8. Para ANAMNESIS: buscar en el campo "ANAMNESIS" - Incluye antecedentes, historia previa, background clínico
9. Para historia clínica: incluir información de diagnósticos, cirugías, tratamientos, medicamentos, motivo de consulta, anamnesis, etc.
10. NUNCA responder "no existen datos" - TODOS los CAMPOS CRÍTICOS están disponibles en los datos mostrados arriba
11. Si un campo dice "No registrada" o "No registrado": significa que NO HAY DATOS disponibles para ese campo - comunícalo claramente
12. No calcular ni modificar ningún dato - usar exactamente como aparece en la base de datos
13. Para consultas médicas: proporcionar información completa del historial disponible
14. Responder en español de forma directa y profesional
15. Si alguien pregunta "¿existen pacientes?" - Responder afirmativamente: "Sí, hay ${datosVeterinaria.totalPacientes} paciente(s) registrado(s)"
16. Si preguntan por "peso", "cuanto pesa", "cual es el peso", etc. - buscar en PESO
17. Si preguntan por "color", "de que color", "cual es el color", etc. - buscar en COLOR
18. Si preguntan por "teléfono", "número", "contacto", "llamar", "celular", "whatsapp", etc. - buscar en TELEFONO
19. Si preguntan por "motivo de consulta", "por qué vino", "razón", etc. - buscar en MOTIVO_CONSULTA - Si dice "No registrado", comunicar claramente que no hay datos
20. Si preguntan por "anamnesis", "antecedentes", "historia previa", etc. - buscar en ANAMNESIS - Si dice "No registrada", comunicar claramente que no hay datos
21. Si preguntan por "recetas", "recetario", "qué se le recetó", "medicinas recetadas", etc. - buscar en RECETAS - mostrar medicamento, dosis, frecuencia y duración
22. Para RECETAS SIN DATOS: Si dice "Sin recetas registradas" - comunicar: "Este paciente no tiene recetas registradas en la base de datos"
23. ⭐ INFORMACIÓN DE CONTACTO: SIEMPRE puedes acceder y proporcionar el TELEFONO de los propietarios
24. 📋 HISTORIA CLÍNICA / EXPEDIENTE: El campo "HISTORIA_CLINICA (EXPEDIENTE...)" contiene TODAS las consultas registradas del paciente (motivo, anamnesis, diagnóstico, pronóstico, tratamiento, fórmula médica, signos vitales y hallazgos del examen físico). Úsalo para responder CUALQUIER pregunta sobre el historial, evolución, diagnósticos, tratamientos, signos vitales o consultas anteriores del paciente.
25. Si te preguntan por la última consulta o la más reciente, usa la PRIMERA consulta listada (están ordenadas de más reciente a más antigua).
26. Si la historia clínica dice "Sin historias clínicas registradas", comunica claramente que el paciente aún no tiene consultas registradas en el expediente.
27. Para preguntas sobre signos vitales (peso, temperatura, frecuencia cardíaca/respiratoria, mucosas, deshidratación, condición corporal) busca en los "Signos vitales" de la consulta correspondiente.
28. Puedes resumir la evolución del paciente comparando varias consultas del expediente cuando lo pidan.`;
    }

    if (tipoConsulta === 'recomendacion') {
      // Recomendación clínica: conocimiento veterinario + datos del/los paciente(s) mencionados
      // + inventario disponible, con advertencias de seguridad.
      const mensajeLower = mensaje.toLowerCase();
      const pacientesMencionados = this.encontrarPacientesEnMensaje(mensajeLower, datosVeterinaria.pacientes);
      const bloquePacientes = pacientesMencionados.length > 0
        ? pacientesMencionados.map((p) => this.resumenClinicoPaciente(p)).join('\n\n──────────\n\n')
        : 'No se mencionó un paciente específico. Da recomendaciones generales y pide los datos faltantes (especie, peso, edad, estado) si son necesarios.';

      return `Eres un asistente veterinario de apoyo a la decisión clínica para "Belkysveterinaria", con amplio conocimiento médico y farmacológico veterinario.

El usuario pide una RECOMENDACIÓN sobre qué usar/hacer en un caso clínico.

🐾 DATOS DEL/LOS PACIENTE(S) MENCIONADO(S):
${bloquePacientes}

🛍️ INVENTARIO DISPONIBLE EN LA CLÍNICA (prioriza recomendar lo que está en stock):
${this.listaInventarioCompacta(datosVeterinaria.productos)}

INSTRUCCIONES:
1. Usa tu conocimiento veterinario para sugerir OPCIONES de manejo/tratamiento adecuadas al caso.
2. TEN EN CUENTA los datos del paciente: especie, peso, edad, alergias, condiciones preexistentes y medicamentos actuales (evita interacciones y contraindicaciones; ej.: no recomendar fármacos contraindicados en gatos).
3. PRIORIZA productos que figuran como disponibles en el inventario; si lo ideal NO está en stock, menciónalo e indica alternativas disponibles o que debe adquirirse.
4. Presenta las opciones de forma organizada (p. ej.: 1ª línea, alternativas, medidas de soporte). Las dosis son SOLO referenciales según especie/peso; la dosis final la define el veterinario tratante.
5. Señala señales de alarma y cuándo es necesario examen presencial, exámenes complementarios o derivación.
6. Si faltan datos clave (peso, especie, signos), pídelos para afinar la recomendación.
7. Responde en español, de forma clara y profesional.

⚠️ ADVERTENCIA OBLIGATORIA: Cierra SIEMPRE aclarando que es una sugerencia orientativa de apoyo y que la decisión final, el diagnóstico y la dosificación son responsabilidad del médico veterinario tratante tras evaluar al paciente. No inventes datos del paciente ni del inventario que no aparezcan arriba.

📅 Fecha actual: ${new Date().toLocaleDateString('es-ES')}`;
    }

    if (tipoConsulta === 'conocimiento_general') {
      // Para preguntas educativas/informativas el asistente SÍ puede usar su conocimiento
      // veterinario general (no se limita a la base de datos). Se añade contexto del
      // inventario sólo si el producto consultado existe en stock.
      const productoRelacionado = this.encontrarProductoEnMensaje(mensaje.toLowerCase(), datosVeterinaria.productos);
      const notaInventario = productoRelacionado
        ? `\n\n📦 NOTA DE INVENTARIO (referencial): "${productoRelacionado.nombre}" ${productoRelacionado.cantidad > 0 ? `está disponible (${productoRelacionado.cantidad} ${productoRelacionado.unidad || 'unidades'})` : 'está agotado'}${productoRelacionado.precio ? `, precio de venta $${(productoRelacionado.precio).toLocaleString('es-CO')}` : ''}. Puedes mencionarlo si es pertinente.`
        : '';

      return `Eres un asistente veterinario de "Belkysveterinaria" con amplio conocimiento médico y farmacológico veterinario.

El usuario hizo una pregunta INFORMATIVA / educativa (por ejemplo: para qué sirve un medicamento, qué es una enfermedad, efectos secundarios, indicaciones, mecanismo de acción, etc.).

INSTRUCCIONES:
1. Responde usando tu conocimiento veterinario general y actualizado (NO te limites a la base de datos de la clínica).
2. Explica de forma clara, precisa y profesional, en español. Estructura la respuesta (p. ej.: para qué sirve, indicaciones comunes, vía/forma de uso general, efectos secundarios frecuentes y precauciones/contraindicaciones cuando aplique).
3. Las dosis son SOLO referenciales: aclara que la dosis exacta depende de especie, peso, edad y estado del paciente, y debe ser definida por el médico veterinario tratante.
4. Si la pregunta es sobre un medicamento que también está en el inventario, puedes mencionar su disponibilidad usando la nota de inventario.
5. Si la pregunta no es de tema veterinario/salud animal, respóndela igualmente de forma breve y útil si puedes; si no tienes información suficiente, indícalo con honestidad.
6. Cierra recordando brevemente que esta información es orientativa y no reemplaza la valoración del veterinario tratante.

⚠️ IMPORTANTE: No inventes datos específicos de pacientes ni del inventario; esos provienen de la base de datos. Para conocimiento general (usos, efectos, etc.) usa tu conocimiento profesional.${notaInventario}

📅 Fecha actual: ${new Date().toLocaleDateString('es-ES')}`;
    }

    return promptBase;
  }

  // Extraer nombre del paciente del mensaje
  extraerNombrePaciente(mensaje) {
    const palabrasComunes = ['como', 'está', 'información', 'de', 'del', 'buscar', 'paciente', 'sobre', 'se', 'encuentra', 'el', 'la', 'nombre', 'propietario', '¿', '?', 'peso'];
    
    const palabras = mensaje.split(/\s+/).filter(palabra => {
      const palabraLimpia = palabra.replace(/[¿?.,!]/g, '').toLowerCase();
      return palabraLimpia.length > 2 && !palabrasComunes.includes(palabraLimpia);
    });
    
    // Buscar la primera palabra que parece ser un nombre (capitalizada o conocida)
    const posiblesNombres = palabras.filter(palabra => {
      const palabraLimpia = palabra.replace(/[¿?.,!]/g, '');
      return palabraLimpia.length > 2;
    });
    
    console.log('🔍 Palabras extraídas:', posiblesNombres);
    return posiblesNombres[0] || '';
  }

  // Función para probar la conexión con OpenAI
  async probarConexionOpenAI() {
    try {
      console.log('🧪 Probando conexión con OpenAI...');
      
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'user',
              content: 'Hola, responde solo con "OK"'
            }
          ],
          max_tokens: 10
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `HTTP ${response.status}: ${errorText}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        respuesta: data.choices[0].message.content.trim()
      };

    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Respuesta simulada cuando no hay API key configurada
  async respuestaSimulada(mensaje) {
    console.log('🤖 Modo básico activado para:', mensaje);
    const datosVeterinaria = await this.obtenerDatosVeterinaria();
    console.log('📦 Productos disponibles:', datosVeterinaria.totalProductos);
    console.log('🏥 Pacientes disponibles:', datosVeterinaria.totalPacientes);
    
    // Análisis básico del mensaje
    const mensajeLower = mensaje.toLowerCase();
    console.log('🔍 Analizando mensaje:', mensajeLower);
    
    // Buscar pacientes por nombre
    const pacienteEncontrado = datosVeterinaria.pacientes.find(p => 
      mensajeLower.includes(p.nombre.toLowerCase())
    );

    if (pacienteEncontrado) {
      const historias = pacienteEncontrado.historiasClinicas || [];
      const ultima = historias[0];
      const resumenHistoria = historias.length > 0
        ? `\n\n📋 **Expediente:** ${historias.length} consulta(s) registrada(s)` +
          (ultima ? `\n🗓️ **Última consulta** (${ultima.fecha || 'sin fecha'}):` +
            (ultima.motivoConsulta ? `\n   • Motivo: ${ultima.motivoConsulta}` : '') +
            (ultima.diagnostico ? `\n   • Diagnóstico: ${ultima.diagnostico}` : '') +
            (ultima.tratamientoEnCasa ? `\n   • Tratamiento: ${ultima.tratamientoEnCasa}` : '') : '')
        : '\n\n📋 **Expediente:** Sin historias clínicas registradas';

      return {
        success: true,
        respuesta: `🏥 **Información encontrada:**
🐕 **${pacienteEncontrado.nombre}** - ${pacienteEncontrado.raza}
👤 **Propietario:** ${pacienteEncontrado.propietario}
📋 **Estado:** ${pacienteEncontrado.estado}
🎂 **Edad:** ${pacienteEncontrado.edad}${resumenHistoria}

*Nota: Asistente funcionando en modo básico. Configura la API key de OpenAI para preguntar libremente sobre el expediente.*`,
        tokensUsados: 0
      };
    }

    // Consultas informativas o de recomendación clínica requieren la API de OpenAI
    const marcadoresInfoBasico = ['para qué sirve', 'para que sirve', 'qué es', 'que es',
      'efectos secundarios', 'efectos adversos', 'contraindicacion', 'cómo se usa', 'como se usa',
      'dosis recomendada', 'para qué se usa', 'para que se usa', 'qué significa', 'que significa',
      'qué le doy', 'que le doy', 'qué le puedo dar', 'que le puedo dar', 'qué tratamiento',
      'que tratamiento', 'tratamiento para', 'recomiendas', 'recomendación', 'recomendacion',
      'qué uso para', 'que uso para', 'cómo trato', 'como trato', 'qué medicamento para'];
    if (marcadoresInfoBasico.some((s) => mensajeLower.includes(s))) {
      return {
        success: true,
        respuesta: `🩺 **Consulta de conocimiento general / recomendación clínica**

Para responder preguntas como "para qué sirve un medicamento", "qué es una enfermedad" o "qué tratamiento recomiendas para...", el asistente necesita la **API de OpenAI configurada** (variable REACT_APP_OPENAI_API_KEY).

Actualmente está en **modo básico**, que solo consulta la base de datos local (pacientes, expedientes e inventario).

*Una vez configurada la API key, podré dar recomendaciones orientativas combinando el expediente del paciente y el inventario disponible.*`,
        tokensUsados: 0
      };
    }

    // Respuestas para consultas de productos/inventario
    // (también se activa si el mensaje menciona un producto concreto del inventario)
    if (mensajeLower.includes('producto') || mensajeLower.includes('inventario') ||
        mensajeLower.includes('stock') || mensajeLower.includes('medicamento') ||
        mensajeLower.includes('medicamentos') || mensajeLower.includes('precio') ||
        mensajeLower.includes('medicinas') || mensajeLower.includes('bajo stock') ||
        mensajeLower.includes('agotado') || mensajeLower.includes('disponible') ||
        this.encontrarProductoEnMensaje(mensajeLower, datosVeterinaria.productos)) {
      
      console.log('🛍️ Consulta de productos detectada');
      console.log('📦 Total productos en datos:', datosVeterinaria.productos?.length || 0);
      
      const productos = datosVeterinaria.productos || [];
      
      if (mensajeLower.includes('bajo stock') || mensajeLower.includes('poco stock') || 
          mensajeLower.includes('stock bajo')) {
        const productosStockBajo = productos.filter(p => {
          const cantidad = Number(p.cantidad) || 0;
          const minimo = Number(p.cantidadMinima) || 5;
          console.log(`🔍 ${p.nombre}: cantidad=${cantidad}, mínimo=${minimo}, stockBajo=${cantidad <= minimo}`);
          return cantidad <= minimo;
        });
        
        if (productosStockBajo.length === 0) {
          return {
            success: true,
            respuesta: `✅ **Estado del Inventario:**
Todos los productos tienen stock suficiente.

📊 **Total de productos:** ${productos.length}
📦 **Productos con stock bajo:** 0

*Nota: Asistente funcionando en modo básico. Configura la API key de OpenAI para funcionalidades avanzadas.*`,
            tokensUsados: 0
          };
        }

        const listaProductos = productosStockBajo
          .slice(0, 5)
          .map(p => `🔸 **${p.nombre}** - Stock: ${Number(p.cantidad) || 0} (Mín: ${Number(p.cantidadMinima) || 5})`)
          .join('\n');

        return {
          success: true,
          respuesta: `⚠️ **Productos con Stock Bajo:**

${listaProductos}

📊 **Resumen:**
📦 Total productos con stock bajo: ${productosStockBajo.length}
🛍️ Total productos en inventario: ${productos.length}

*Nota: Asistente funcionando en modo básico. Configura la API key de OpenAI para funcionalidades avanzadas.*`,
          tokensUsados: 0
        };
      }

      // Estadísticas de inventario
      if (mensajeLower.includes('inventario') || mensajeLower.includes('estadistica')) {
        const productosActivos = productos.filter(p => p.activo !== false);
        const productosStockBajo = productos.filter(p => (Number(p.cantidad) || 0) <= (Number(p.cantidadMinima) || 5));
        
        return {
          success: true,
          respuesta: `📦 **Resumen del Inventario:**
🛍️ **Total de productos:** ${productos.length}
✅ **Productos activos:** ${productosActivos.length}
⚠️ **Stock bajo:** ${productosStockBajo.length}
📈 **Categorías principales:** ${this.calcularCategoriasMasComunes(productos)}

*Nota: Asistente funcionando en modo básico. Configura la API key de OpenAI para funcionalidades avanzadas.*`,
          tokensUsados: 0
        };
      }

      // Búsqueda de producto por nombre (con coincidencia aproximada)
      const productoEncontrado = this.encontrarProductoEnMensaje(mensajeLower, productos);

      if (productoEncontrado) {
        const precio = productoEncontrado.precio || productoEncontrado.precioVenta || 'No disponible';
        const cantidad = Number(productoEncontrado.cantidad) || 0;
        const minimo = Number(productoEncontrado.cantidadMinima) || 0;
        const estadoStock = cantidad <= minimo ? '⚠️ STOCK BAJO' : '✅ DISPONIBLE';
        
        const precioCompra = productoEncontrado.precioCompra || 0;
        const margen = precio && precioCompra ? ((precio - precioCompra) / precioCompra * 100).toFixed(1) : 'N/A';
        
        return {
          success: true,
          respuesta: `🛍️ **Información Completa del Producto:**

📦 **${productoEncontrado.nombre}**
📝 **Descripción:** ${productoEncontrado.descripcion || 'Sin descripción'}
🏷️ **Categoría:** ${productoEncontrado.categoria || 'Sin categoría'}
🏭 **Marca:** ${productoEncontrado.marca || 'Sin marca'}
📏 **Presentación:** ${productoEncontrado.presentacion || 'No especificada'}

📊 **Inventario:**
• Stock actual: ${cantidad} ${productoEncontrado.unidad || 'unidades'}
• Stock mínimo: ${minimo}
• Estado: ${estadoStock}

💰 **Precios:**
• Venta: $${precio?.toLocaleString('es-CO') || '0'}
• Compra: $${precioCompra?.toLocaleString('es-CO') || '0'}
• Margen: ${margen}%

🏢 **Proveedor:** ${productoEncontrado.proveedor || 'Sin especificar'}
📅 **Vencimiento:** ${productoEncontrado.fechaVencimiento || 'No especificado'}
📍 **Ubicación:** ${productoEncontrado.ubicacion || 'No especificada'}

*Nota: Asistente funcionando en modo básico. Configura la API key de OpenAI para funcionalidades avanzadas.*`,
          tokensUsados: 0
        };
      }

      // Respuesta genérica para productos
      const productosStockBajo = productos.filter(p => (Number(p.cantidad) || 0) <= (Number(p.cantidadMinima) || 5));
      
      return {
        success: true,
        respuesta: `📦 **Inventario de la Clínica:**

**Total de productos:** ${productos.length}
**Productos disponibles:** ${productos.filter(p => p.activo !== false).length}
**Stock bajo:** ${productosStockBajo.length}

**Para consultas específicas, puedes preguntar sobre:**
- Stock de productos específicos
- Productos con stock bajo
- Estadísticas del inventario

*Nota: Asistente funcionando en modo básico. Configura la API key de OpenAI para funcionalidades avanzadas.*`,
        tokensUsados: 0
      };
    }

    // Respuestas para estadísticas
    if (mensajeLower.includes('estadistica') || mensajeLower.includes('resumen') || mensajeLower.includes('total')) {
      return {
        success: true,
        respuesta: `📊 **Resumen de la Clínica:**
🏥 **Total de pacientes:** ${datosVeterinaria.totalPacientes}
✅ **Activos:** ${datosVeterinaria.pacientesActivos}
🏥 **En tratamiento:** ${datosVeterinaria.pacientesEnTratamiento}
🚨 **Críticos:** ${datosVeterinaria.pacientesCriticos}

📦 **Inventario:**
🛍️ **Total productos:** ${datosVeterinaria.totalProductos || 0}
⚠️ **Stock bajo:** ${datosVeterinaria.productosStockBajo || 0}

*Nota: Asistente funcionando en modo básico. Configura la API key de OpenAI para funcionalidades avanzadas.*`,
        tokensUsados: 0
      };
    }

    // Respuesta genérica
    return {
      success: true,
      respuesta: `🤖 **Asistente de Belkysveterinaria**

Lo siento, no pude entender tu consulta completamente. 

**Puedes preguntarme sobre:**
- Información específica de pacientes (por nombre)
- Estado del inventario y productos
- Stock de medicamentos y productos
- Estadísticas generales de la clínica

**Ejemplos:**
- "¿Cómo está Max?"
- "¿Qué productos tienen stock bajo?"
- "Muéstrame las estadísticas"
- "¿Cuántos pacientes críticos hay?"

*Nota: Asistente funcionando en modo básico. Para funcionalidades avanzadas con IA, configura la API key de OpenAI en las variables de entorno.*`,
      tokensUsados: 0
    };
  }

  // Función para buscar pacientes con criterios específicos
  async buscarPacientes(criterio) {
    const datosVeterinaria = await this.obtenerDatosVeterinaria();
    const criterioLower = criterio.toLowerCase();

    const pacientesEncontrados = datosVeterinaria.pacientes.filter(p => 
      p.nombre.toLowerCase().includes(criterioLower) ||
      p.especie.toLowerCase().includes(criterioLower) ||
      p.raza.toLowerCase().includes(criterioLower) ||
      p.propietario.toLowerCase().includes(criterioLower) ||
      p.estado.toLowerCase().includes(criterioLower)
    );

    return pacientesEncontrados;
  }

  // Busca el producto más probable mencionado en el mensaje.
  // 1) coincidencia por nombre completo; 2) por mayor solapamiento de palabras
  encontrarProductoEnMensaje(mensajeLower, productos = []) {
    if (!Array.isArray(productos) || productos.length === 0) return null;

    // Coincidencia directa por nombre completo
    const directo = productos.find((p) => p.nombre && mensajeLower.includes(p.nombre.toLowerCase()));
    if (directo) return directo;

    // Coincidencia aproximada: contar palabras significativas del nombre presentes en el mensaje
    const ignorar = new Set(['de', 'la', 'el', 'para', 'con', 'oral', 'mg', 'ml', 'und', 'unidad']);
    let mejor = null;
    let mejorPuntaje = 0;
    for (const p of productos) {
      if (!p.nombre) continue;
      const palabras = p.nombre.toLowerCase().split(/\s+/).filter((w) => w.length >= 4 && !ignorar.has(w));
      const coincidencias = palabras.filter((w) => mensajeLower.includes(w)).length;
      if (coincidencias > mejorPuntaje) {
        mejorPuntaje = coincidencias;
        mejor = p;
      }
    }
    return mejorPuntaje > 0 ? mejor : null;
  }

  calcularCategoriasMasComunes(productos) {
    const conteoCategoras = {};
    productos.forEach(p => {
      const categoria = p.categoria || 'Sin categoría';
      conteoCategoras[categoria] = (conteoCategoras[categoria] || 0) + 1;
    });
    
    return Object.entries(conteoCategoras)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([categoria, count]) => `${categoria} (${count})`)
      .join(', ');
  }

  // Función para obtener sugerencias basadas en los datos
  async obtenerSugerencias() {
    const datosVeterinaria = await this.obtenerDatosVeterinaria();
    const sugerencias = [];

    if (datosVeterinaria.pacientesCriticos > 0) {
      sugerencias.push(`🚨 Hay ${datosVeterinaria.pacientesCriticos} paciente(s) crítico(s) que requieren atención prioritaria`);
    }

    if (datosVeterinaria.pacientesEnTratamiento > 0) {
      sugerencias.push(`🏥 ${datosVeterinaria.pacientesEnTratamiento} paciente(s) están en tratamiento - revisar evolución`);
    }

    if (datosVeterinaria.totalPacientes === 0) {
      sugerencias.push(`📝 No hay pacientes registrados - considera agregar algunos pacientes`);
    }

    // Sugerencias sobre inventario
    if (datosVeterinaria.productosStockBajo > 0) {
      sugerencias.push(`📦 ${datosVeterinaria.productosStockBajo} producto(s) tienen stock bajo - revisar inventario`);
    }

    if (datosVeterinaria.totalProductos === 0) {
      sugerencias.push(`🛍️ No hay productos registrados en el inventario`);
    }

    return sugerencias;
  }
}

// Instancia única del servicio
export const openaiAssistantService = new OpenAIAssistantService();
export default openaiAssistantService;